// src/views/AccommodationProvider/ProviderPropertyOwnership.js
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardBody, 
  CardTitle, 
  Badge, 
  Row, 
  Col, 
  Button, 
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from '../../api/Axios';

const ProviderPropertyOwnership = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams();
  
  // Get provider information from localStorage
  const providerType = localStorage.getItem('provider_type');
  const providerName = localStorage.getItem('provider_name');
  const representativeName = localStorage.getItem('representative_name');
  const regStatus = localStorage.getItem('reg_status');

  // State management
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ type: '', message: '', visible: false });
  const [landUseAlert, setLandUseAlert] = useState({ type: '', message: '', visible: false });
  const [files, setFiles] = useState({
    ownership: null
  });
  const [landUseFiles, setLandUseFiles] = useState({
    zoning_certificate: null,
    consent_use: null,
    occupancy_certificate: null
  });
  const [existingDocuments, setExistingDocuments] = useState({
    ownership: null,
    ownership_status: 'not_provided',
    zoning_certificate: null,
    consent_use: null,
    occupancy_certificate: null,
    zoning_certificate_status: 'not_provided',
    consent_use_status: 'not_provided',
    occupancy_certificate_status: 'not_provided'
  });
  
  // Checkbox and form state - Ownership Documents (Left Column)
  const [uploadTitleDeed, setUploadTitleDeed] = useState(false);
  const [uploadLeaseAgreement, setUploadLeaseAgreement] = useState(false);
  const [leasePermitsStudentHousing, setLeasePermitsStudentHousing] = useState(false);
  const [leasePermissionPageNo, setLeasePermissionPageNo] = useState('');
  
  // Checkbox and form state - Land Use Documents (Right Column)
  const [uploadZoningCert, setUploadZoningCert] = useState(false);
  const [uploadConsentUse, setUploadConsentUse] = useState(false);
  const [uploadOccupancyCert, setUploadOccupancyCert] = useState(false);

  // Load property details and existing documents
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!propertyId) {
        setAlert({
          type: 'danger',
          message: 'Property ID is required.',
          visible: true
        });
        setLoading(false);
        return;
      }

      try {
        // Fetch property details
        const propertyResponse = await axiosInstance.get(`/api/properties/${propertyId}/`);
        setProperty(propertyResponse.data);

        // Fetch existing ownership and zoning documents
        try {
          const documentsResponse = await axiosInstance.get(`/api/properties/${propertyId}/ownership-zoning/`);
          setExistingDocuments(documentsResponse.data);
        } catch (docError) {
          // Documents might not exist yet, that's okay
          console.log('No existing documents found');
        }

      } catch (error) {
        console.error('Error fetching property data:', error);
        setAlert({
          type: 'danger',
          message: 'Failed to load property information. Please try again.',
          visible: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPropertyData();
  }, [propertyId]);

  // Determine welcome message based on provider type
  const getWelcomeMessage = () => {
    if (providerType === 'individual') {
      return `Ownership Documents - ${providerName || 'Individual Provider'}`;
    } else if (providerType === 'entity') {
      return `Ownership Documents - ${providerName || 'Your Organization'}`;
    } else {
      return 'Ownership Documents';
    }
  };

  const getSubtitleMessage = () => {
    if (providerType === 'entity' && representativeName) {
      return `Representative: ${representativeName}`;
    }
    return null;
  };

  const getProviderTypeDisplay = () => {
    if (providerType === 'individual') {
      return 'Individual Property Owner';
    } else if (providerType === 'entity') {
      return 'Business/Organization';
    }
    return 'Accommodation Provider';
  };

  const handleFileChange = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Update the state first to show the selected file
    setFiles(prevFiles => ({
      ...prevFiles,
      [documentType]: file
    }));

    // Auto-upload the file
    await uploadOwnershipDocument(file, documentType);
  };

  const uploadOwnershipDocument = async (file, documentType) => {
    setIsSubmitting(true);
    setAlert({ type: '', message: '', visible: false });

    // Validate ownership type is selected
    if (!uploadTitleDeed && !uploadLeaseAgreement) {
      setAlert({
        type: 'warning',
        message: 'Please select document type (Title Deed or Lease Agreement) before uploading.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    // Validate lease requirements
    if (uploadLeaseAgreement && !leasePermitsStudentHousing) {
      setAlert({
        type: 'warning',
        message: 'Please confirm that the lease permits operating property as student housing.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    if (uploadLeaseAgreement && leasePermitsStudentHousing && !leasePermissionPageNo.trim()) {
      setAlert({
        type: 'warning',
        message: 'Please specify the page number where student housing permission is mentioned.',
        visible: true
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('ownership', file);
      
      // Include ownership type
      if (uploadTitleDeed) {
        formData.append('ownership_type', 'deed');
      } else if (uploadLeaseAgreement) {
        formData.append('ownership_type', 'lease');
        if (leasePermissionPageNo && leasePermissionPageNo.trim() !== '') {
          formData.append('lease_permission_page', leasePermissionPageNo.trim());
        }
      }

      const response = await axiosInstance.post(
        `/api/properties/${propertyId}/ownership-zoning/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data) {
        setAlert({
          type: 'success',
          message: 'Ownership document uploaded successfully! It is now under review.',
          visible: true
        });

        // Update existing documents state
        if (response.data.data) {
          setExistingDocuments(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error uploading ownership document:', error);
      if (error.response?.status === 401) {
        setAlert({
          type: 'danger',
          message: 'Authentication required. Please log in again.',
          visible: true
        });
      } else {
        setAlert({
          type: 'danger',
          message: error.response?.data?.error || 'Failed to upload ownership document. Please try again.',
          visible: true
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLandUseFileChange = async (documentType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Update the state first to show the selected file
    setLandUseFiles(prevFiles => ({
      ...prevFiles,
      [documentType]: file
    }));

    // Auto-upload the file
    await uploadLandUseDocument(file, documentType);
  };

  const uploadLandUseDocument = async (file, documentType) => {
    setIsSubmitting(true);
    setLandUseAlert({ type: '', message: '', visible: false });

    try {
      const formData = new FormData();
      formData.append(documentType, file);

      const response = await axiosInstance.post(
        `/api/properties/${propertyId}/ownership-zoning/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data) {
        const documentTypeName = {
          'zoning_certificate': 'Zoning Certificate',
          'consent_use': 'Consent Use Document',
          'occupancy_certificate': 'Occupancy Certificate'
        }[documentType] || 'Land Use Document';

        setLandUseAlert({
          type: 'success',
          message: `${documentTypeName} uploaded successfully! It is now under review.`,
          visible: true
        });

        // Update existing documents state
        if (response.data.data) {
          setExistingDocuments(response.data.data);
        }
      }
    } catch (error) {
      console.error('Error uploading land use document:', error);
      if (error.response?.status === 401) {
        setLandUseAlert({
          type: 'danger',
          message: 'Authentication required. Please log in again.',
          visible: true
        });
      } else {
        setLandUseAlert({
          type: 'danger',
          message: error.response?.data?.error || 'Failed to upload land use document. Please try again.',
          visible: true
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };



  const handleBackToInspection = () => {
    navigate('/provider-request-inspection');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'not_provided': { color: 'secondary', text: 'Not Provided' },
      'pending_approval': { color: 'warning', text: 'Pending Approval' },
      'rejected': { color:'danger', text: 'Rejected' },
      'accepted': { color: 'success', text: 'Accepted' }
    };
    
    const config = statusConfig[status] || statusConfig['not_provided'];
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const getLandUseDocumentStatus = () => {
    // Check all land use document statuses
    const statuses = [
      existingDocuments.zoning_certificate_status,
      existingDocuments.consent_use_status,
      existingDocuments.occupancy_certificate_status
    ].filter(status => status && status !== 'not_provided');

    if (statuses.length === 0) {
      return 'not_provided';
    }

    // If any document is rejected, show rejected
    if (statuses.some(status => status === 'rejected')) {
      return 'rejected';
    }

    // If any document is pending, show pending
    if (statuses.some(status => status === 'pending_approval')) {
      return 'pending_approval';
    }

    // If all provided documents are accepted
    if (statuses.every(status => status === 'accepted')) {
      return 'accepted';
    }

    // Default fallback
    return 'not_provided';
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <Spinner color="primary" />
          <p className="mt-2">Loading property information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header Section */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{getWelcomeMessage()}</h2>
              {getSubtitleMessage() && (
                <p className="text-muted mb-1">{getSubtitleMessage()}</p>
              )}
              <Badge color="info" className="me-2">
                {getProviderTypeDisplay()}
              </Badge>
              {property && (
                <Badge color="primary">
                  Property: {property.trading_name}
                </Badge>
              )}
            </div>
            <Button 
              color="outline-secondary" 
              onClick={handleBackToInspection}
              className="btn-sm"
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Request Inspection
            </Button>
          </div>
          
          {/* Information Text */}
          <div className="alert alert-info mb-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Upload Required Documents:</strong> Ownership documents (Title Deed or Lease Agreement with minimum 24 months remaining) and Land Use documents (Zoning Certificate, Consent Use, or Occupancy Certificate).
          </div>
          
          {/* Document Status Information */}
          <div className="alert alert-light border mb-4">
            <h6 className="mb-2">
              <i className="bi bi-clipboard-check me-2"></i>
              Document Review Status
            </h6>
            <ul className="mb-0 small">
              <li><Badge color="warning">Pending Approval</Badge> - Your document is being reviewed by our team</li>
              <li><Badge color="success">Accepted</Badge> - Document approved. Expiry date shown if applicable</li>
              <li><Badge color="danger">Rejected</Badge> - Document needs correction. See rejection reason below</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Ownership and Zoning Documents Form */}
      <Row className="mb-4">
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                Upload Ownership Documents
              </CardTitle>
              
              {/* Alert */}
              {alert.visible && (
                <Alert color={alert.type} toggle={() => setAlert({...alert, visible: false})} className="mb-4">
                  {alert.message}
                </Alert>
              )}

              {/* Existing Documents Status */}
              <Row className="mb-4">
                <Col md="8" className="mx-auto">
                  <Card className="border-light bg-light">
                    <CardBody>
                      <h6 className="mb-2">
                        <i className="bi bi-file-text me-2"></i>
                        Ownership Document Status
                      </h6>
                      {getStatusBadge(existingDocuments.ownership_status)}
                      {existingDocuments.ownership && (
                        <div className="mt-2">
                          <small className="text-muted">
                            Document uploaded: {new Date().toLocaleDateString()}
                          </small>
                        </div>
                      )}
                      
                      {/* Display expiry date if approved */}
                      {existingDocuments.ownership_status === 'accepted' && existingDocuments.ownership_expiry_date && (
                        <div className="mt-2">
                          <small className="text-success">
                            <i className="bi bi-calendar-check me-1"></i>
                            Valid until: {new Date(existingDocuments.ownership_expiry_date).toLocaleDateString()}
                          </small>
                        </div>
                      )}
                      
                      {/* Display rejection comments if rejected */}
                      {existingDocuments.ownership_status === 'rejected' && existingDocuments.ownership_comments && (
                        <div className="mt-2">
                          <small className="text-danger">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            <strong>Rejection reason:</strong> {existingDocuments.ownership_comments}
                          </small>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              
              <Form>
                {/* Checkbox Options */}
                <Row className="mb-4">
                  <Col md="12">
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="titleDeedCheckbox"
                        checked={uploadTitleDeed}
                        onChange={(e) => {
                          setUploadTitleDeed(e.target.checked);
                          if (e.target.checked) {
                            setUploadLeaseAgreement(false);
                            setLeasePermitsStudentHousing(false);
                            setLeasePermissionPageNo('');
                            setFiles({ ownership: null });
                          }
                        }}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <Label check for="titleDeedCheckbox" className="ms-2">
                        <strong>Upload Title Deed</strong>
                      </Label>
                    </FormGroup>
                    
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="leaseAgreementCheckbox"
                        checked={uploadLeaseAgreement}
                        onChange={(e) => {
                          setUploadLeaseAgreement(e.target.checked);
                          if (e.target.checked) {
                            setUploadTitleDeed(false);
                            setFiles({ ownership: null });
                          } else {
                            setLeasePermitsStudentHousing(false);
                            setLeasePermissionPageNo('');
                          }
                        }}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <Label check for="leaseAgreementCheckbox" className="ms-2">
                        <strong>Upload Lease Agreement (minimum 24 months)</strong>
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Title Deed Upload Component */}
                {uploadTitleDeed && (
                  <Row className="mb-4">
                    <Col md="12" className="mx-auto">
                      <Card className="border-primary">
                        <CardBody>
                          <FormGroup>
                            <Label for="title_deed">
                              <i className="bi bi-file-text me-2"></i>
                              Upload Title Deed Document
                            </Label>
                            <Input
                              type="file"
                              name="title_deed"
                              id="title_deed"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => handleFileChange('ownership', e)}
                              disabled={regStatus !== '2' || isSubmitting}
                            />
                            <small className="text-muted">
                              For property owners - Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                            </small>
                            {files.ownership && (
                              <div className="mt-2">
                                <Badge color="info">
                                  <i className="bi bi-paperclip me-1"></i>
                                  {files.ownership.name}
                                </Badge>
                              </div>
                            )}
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Lease Agreement Flow */}
                {uploadLeaseAgreement && (
                  <>
                    <Row className="mb-4">
                      <Col md="12" className="mx-auto">
                        <Card className="border-warning">
                          <CardBody>
                            <FormGroup check className="mb-3">
                              <Input
                                type="checkbox"
                                id="leasePermitsCheckbox"
                                checked={leasePermitsStudentHousing}
                                onChange={(e) => {
                                  setLeasePermitsStudentHousing(e.target.checked);
                                  if (!e.target.checked) {
                                    setLeasePermissionPageNo('');
                                    setFiles({ ownership: null });
                                  }
                                }}
                                disabled={regStatus !== '2' || isSubmitting}
                              />
                              <Label check for="leasePermitsCheckbox" className="ms-2">
                                <strong>Lease permits operating property as student housing</strong>
                              </Label>
                            </FormGroup>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>

                    {/* Page Number Input */}
                    {leasePermitsStudentHousing && (
                      <Row className="mb-4">
                        <Col md="12" className="mx-auto">
                          <Card className="border-info">
                            <CardBody>
                              <FormGroup>
                                <Label for="pageNumber">
                                  <i className="bi bi-file-earmark-text me-2"></i>
                                  Page number of this permission on the lease
                                </Label>
                                <Input
                                  type="text"
                                  name="pageNumber"
                                  id="pageNumber"
                                  value={leasePermissionPageNo}
                                  onChange={(e) => setLeasePermissionPageNo(e.target.value)}
                                  placeholder="e.g., Page 5 or Section 3.2"
                                  disabled={regStatus !== '2' || isSubmitting}
                                />
                                <small className="text-muted">
                                  Specify the page or section where student housing permission is mentioned
                                </small>
                              </FormGroup>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    )}

                    {/* Lease Upload Component */}
                    {leasePermitsStudentHousing && leasePermissionPageNo.trim() !== '' && (
                      <Row className="mb-4">
                        <Col md="12" className="mx-auto">
                          <Card className="border-success">
                            <CardBody>
                              <FormGroup>
                                <Label for="lease_agreement">
                                  <i className="bi bi-file-text me-2"></i>
                                  Upload Lease Agreement Document
                                </Label>
                                <Input
                                  type="file"
                                  name="lease_agreement"
                                  id="lease_agreement"
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  onChange={(e) => handleFileChange('ownership', e)}
                                  disabled={regStatus !== '2' || isSubmitting}
                                />
                                <small className="text-muted">
                                  Minimum 24 months remaining - Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                                </small>
                                {files.ownership && (
                                  <div className="mt-2">
                                    <Badge color="success">
                                      <i className="bi bi-paperclip me-1"></i>
                                      {files.ownership.name}
                                    </Badge>
                                  </div>
                                )}
                              </FormGroup>
                            </CardBody>
                          </Card>
                        </Col>
                      </Row>
                    )}
                  </>
                )}

                <hr className="my-4" />

                <Row>
                  <Col md="12" className="text-center">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Documents will upload automatically when you select them.
                    </small>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
        <Col md="6">
          <Card>
            <CardBody>
              <CardTitle tag="h4" className="mb-4">
                <i className="bi bi-file-earmark-text me-2 text-primary"></i>
                Upload Land Use Documents
              </CardTitle>
              
              {/* Alert */}
              {landUseAlert.visible && (
                <Alert color={landUseAlert.type} toggle={() => setLandUseAlert({...landUseAlert, visible: false})} className="mb-4">
                  {landUseAlert.message}
                </Alert>
              )}

              {/* Existing Documents Status */}
              <Row className="mb-4">
                <Col md="12">
                  <Card className="border-light bg-light">
                    <CardBody>
                      <h6 className="mb-3">
                        <i className="bi bi-file-earmark-check me-2"></i>
                        Land Use Document Status
                      </h6>
                      
                      {/* Zoning Certificate Status */}
                      {existingDocuments.zoning_certificate_status !== 'not_provided' && (
                        <div className="mb-3 p-2 border rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Zoning Certificate:</strong>
                            {getStatusBadge(existingDocuments.zoning_certificate_status)}
                          </div>
                          {existingDocuments.zoning_certificate_status === 'accepted' && existingDocuments.zoning_certificate_expiry_date && (
                            <small className="text-success">
                              <i className="bi bi-calendar-check me-1"></i>
                              Valid until: {new Date(existingDocuments.zoning_certificate_expiry_date).toLocaleDateString()}
                            </small>
                          )}
                          {existingDocuments.zoning_certificate_status === 'rejected' && existingDocuments.zoning_certificate_comments && (
                            <small className="text-danger">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              <strong>Rejection reason:</strong> {existingDocuments.zoning_certificate_comments}
                            </small>
                          )}
                        </div>
                      )}
                      
                      {/* Consent Use Status */}
                      {existingDocuments.consent_use_status !== 'not_provided' && (
                        <div className="mb-3 p-2 border rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Consent of Use:</strong>
                            {getStatusBadge(existingDocuments.consent_use_status)}
                          </div>
                          {existingDocuments.consent_use_status === 'accepted' && existingDocuments.consent_use_expiry_date && (
                            <small className="text-success">
                              <i className="bi bi-calendar-check me-1"></i>
                              Valid until: {new Date(existingDocuments.consent_use_expiry_date).toLocaleDateString()}
                            </small>
                          )}
                          {existingDocuments.consent_use_status === 'rejected' && existingDocuments.consent_use_comments && (
                            <small className="text-danger">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              <strong>Rejection reason:</strong> {existingDocuments.consent_use_comments}
                            </small>
                          )}
                        </div>
                      )}
                      
                      {/* Occupancy Certificate Status */}
                      {existingDocuments.occupancy_certificate_status !== 'not_provided' && (
                        <div className="mb-3 p-2 border rounded">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <strong>Occupancy Certificate:</strong>
                            {getStatusBadge(existingDocuments.occupancy_certificate_status)}
                          </div>
                          {existingDocuments.occupancy_certificate_status === 'accepted' && existingDocuments.occupancy_certificate_expiry_date && (
                            <small className="text-success">
                              <i className="bi bi-calendar-check me-1"></i>
                              Valid until: {new Date(existingDocuments.occupancy_certificate_expiry_date).toLocaleDateString()}
                            </small>
                          )}
                          {existingDocuments.occupancy_certificate_status === 'rejected' && existingDocuments.occupancy_certificate_comments && (
                            <small className="text-danger">
                              <i className="bi bi-exclamation-triangle me-1"></i>
                              <strong>Rejection reason:</strong> {existingDocuments.occupancy_certificate_comments}
                            </small>
                          )}
                        </div>
                      )}
                      
                      {/* Show overall status if no documents uploaded */}
                      {getLandUseDocumentStatus() === 'not_provided' && (
                        <div>
                          {getStatusBadge(getLandUseDocumentStatus())}
                          <div className="mt-2">
                            <small className="text-muted">
                              No land use documents uploaded yet
                            </small>
                          </div>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </Col>
              </Row>
              
              <Form>
                {/* Checkbox Options */}
                <Row className="mb-4">
                  <Col md="12">
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="zoningCertCheckbox"
                        checked={uploadZoningCert}
                        onChange={(e) => {
                          setUploadZoningCert(e.target.checked);
                          if (e.target.checked) {
                            setUploadConsentUse(false);
                            setUploadOccupancyCert(false);
                            setLandUseFiles({ zoning_certificate: null, consent_use: null, occupancy_certificate: null });
                          }
                        }}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <Label check for="zoningCertCheckbox" className="ms-2">
                        <strong>Upload Zoning Certificate</strong>
                      </Label>
                    </FormGroup>
                    
                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="consentUseCheckbox"
                        checked={uploadConsentUse}
                        onChange={(e) => {
                          setUploadConsentUse(e.target.checked);
                          if (e.target.checked) {
                            setUploadZoningCert(false);
                            setUploadOccupancyCert(false);
                            setLandUseFiles({ zoning_certificate: null, consent_use: null, occupancy_certificate: null });
                          }
                        }}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <Label check for="consentUseCheckbox" className="ms-2">
                        <strong>Consent Use (approved plans)</strong>
                      </Label>
                    </FormGroup>

                    <FormGroup check className="mb-3">
                      <Input
                        type="checkbox"
                        id="occupancyCertCheckbox"
                        checked={uploadOccupancyCert}
                        onChange={(e) => {
                          setUploadOccupancyCert(e.target.checked);
                          if (e.target.checked) {
                            setUploadZoningCert(false);
                            setUploadConsentUse(false);
                            setLandUseFiles({ zoning_certificate: null, consent_use: null, occupancy_certificate: null });
                          }
                        }}
                        disabled={regStatus !== '2' || isSubmitting}
                      />
                      <Label check for="occupancyCertCheckbox" className="ms-2">
                        <strong>Occupancy Certificate</strong>
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zoning Certificate Upload Component */}
                {uploadZoningCert && (
                  <Row className="mb-4">
                    <Col md="12" className="mx-auto">
                      <Card className="border-primary">
                        <CardBody>
                          <FormGroup>
                            <Label for="zoning_certificate">
                              <i className="bi bi-file-text me-2"></i>
                              Upload Zoning Certificate Document
                            </Label>
                            <Input
                              type="file"
                              name="zoning_certificate"
                              id="zoning_certificate"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => handleLandUseFileChange('zoning_certificate', e)}
                              disabled={regStatus !== '2' || isSubmitting}
                            />
                            <small className="text-muted">
                              Zoning Certificate - Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                            </small>
                            {landUseFiles.zoning_certificate && (
                              <div className="mt-2">
                                <Badge color="info">
                                  <i className="bi bi-paperclip me-1"></i>
                                  {landUseFiles.zoning_certificate.name}
                                </Badge>
                              </div>
                            )}
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Consent Use Upload Component */}
                {uploadConsentUse && (
                  <Row className="mb-4">
                    <Col md="12" className="mx-auto">
                      <Card className="border-success">
                        <CardBody>
                          <FormGroup>
                            <Label for="consent_use">
                              <i className="bi bi-file-text me-2"></i>
                              Upload Consent Use Document
                            </Label>
                            <Input
                              type="file"
                              name="consent_use"
                              id="consent_use"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => handleLandUseFileChange('consent_use', e)}
                              disabled={regStatus !== '2' || isSubmitting}
                            />
                            <small className="text-muted">
                              Consent Use (approved plans) - Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                            </small>
                            {landUseFiles.consent_use && (
                              <div className="mt-2">
                                <Badge color="success">
                                  <i className="bi bi-paperclip me-1"></i>
                                  {landUseFiles.consent_use.name}
                                </Badge>
                              </div>
                            )}
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

                {/* Occupancy Certificate Upload Component */}
                {uploadOccupancyCert && (
                  <Row className="mb-4">
                    <Col md="12" className="mx-auto">
                      <Card className="border-warning">
                        <CardBody>
                          <FormGroup>
                            <Label for="occupancy_certificate">
                              <i className="bi bi-file-text me-2"></i>
                              Upload Occupancy Certificate Document
                            </Label>
                            <Input
                              type="file"
                              name="occupancy_certificate"
                              id="occupancy_certificate"
                              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                              onChange={(e) => handleLandUseFileChange('occupancy_certificate', e)}
                              disabled={regStatus !== '2' || isSubmitting}
                            />
                            <small className="text-muted">
                              Occupancy Certificate - Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
                            </small>
                            {landUseFiles.occupancy_certificate && (
                              <div className="mt-2">
                                <Badge color="warning">
                                  <i className="bi bi-paperclip me-1"></i>
                                  {landUseFiles.occupancy_certificate.name}
                                </Badge>
                              </div>
                            )}
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                )}

                <hr className="my-4" />

                <Row>
                  <Col md="12" className="text-center">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      Documents will upload automatically when you select them.
                    </small>
                  </Col>
                </Row>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Help Section */}
      <Row>
        <Col md="12">
          <Card className="border-0 bg-light">
            <CardBody>
              <CardTitle tag="h6" className="mb-3">
                <i className="bi bi-lightbulb me-2"></i>
                Upload Guidelines
              </CardTitle>
              <Row>
                <Col md="6">
                  <div className="mb-2">
                    <strong>Document Requirements:</strong>
                  </div>
                  <ul className="small text-muted mb-0">
                    <li><strong>Ownership Documents:</strong> Title Deed or Lease Agreement (minimum 24 months)</li>
                    <li><strong>Land Use Documents:</strong> Zoning Certificate, Consent Use, or Occupancy Certificate</li>
                    <li>Documents must be clear and legible</li>
                    <li>All pages must be included</li>
                    <li>Maximum file size: 10MB per document</li>
                  </ul>
                </Col>
                <Col md="6">
                  <div className="mb-2">
                    <strong>Next Steps:</strong>
                  </div>
                  <div className="small text-muted">
                    After uploading your documents, they will be reviewed by our team. 
                    You can proceed to complete Operational Standards and Policies while waiting for approval.
                    Once documents are approved, you can request a property inspection.
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>

    </div>
  );
};

export default ProviderPropertyOwnership;